import { onlineManager } from '@tanstack/react-query'
import useEventTopicsStore from '@/store/eventTopics.ts'
import { getFullApiUrl } from '@/config/apiConfig'
import { isNativePlatform } from '@/lib/platform/platformDetection'
import { api } from '@/lib/api-client'
import { adoptSignedMediaUrlsInJson } from '@/lib/media/adoptSignedMediaUrls'
// Dev-harness Mercure registry. Symbol uses stay behind the compile-time
// harness guard so Rollup can remove this module graph from production builds.
import {
  registerMercureHandler,
  clearMercureRegistry,
} from '@/lib/devtools/harness/domains/offline/mercureRegistry'

type MercureMessageHandler = (event: MessageEvent) => void

type SubscriptionRecord = {
  handler: MercureMessageHandler
  onRecovery: (() => void) | null
  closed: boolean
}

type ManagedConnection = {
  topic: string
  subscriptions: Map<number, SubscriptionRecord>
  eventSource: EventSource | null
  reconnectTimer: ReturnType<typeof setTimeout> | null
  tokenRefreshTimer: ReturnType<typeof setTimeout> | null
  generation: number
  reconnectAttempts: number
  lastEventId: string | null
  /**
   * Generation that owes a recovery dispatch on open, or null when none is
   * pending. Bound to a generation rather than a boolean so that a source
   * which is about to be replaced cannot consume the recovery meant for its
   * replacement when its own onopen lands first.
   */
  recoveryAfterGeneration: number | null
}

export type MercureSubscription = {
  readonly closed: boolean
  close: () => void
}

export type MercureSubscriptionOptions = {
  /** Refetch authoritative state after every manager-created reconnect. */
  onRecovery?: () => void
}

let mercureJwtToken: string | null = null
let tokenExpiry: number | null = null
let tokenFetchPromise: Promise<string | null> | null = null
let tokenGeneration = 0
let nextSubscriptionId = 1

const connections = new Map<string, ManagedConnection>()

// The backend token lifetime is one hour. Refresh five minutes before expiry.
const TOKEN_LIFETIME_MS = 3600 * 1000
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000
const VISIBILITY_RECONNECT_THRESHOLD_MS = 30 * 1000
const RECONNECT_STAGGER_MS = 100
const RECONNECT_BASE_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 30 * 1000

const isDev = import.meta.env.DEV
const debug = (...args: unknown[]) => isDev && console.log('[Mercure]', ...args)

let lifecycleListenersInstalled = false
let hiddenAt: number | null = null
let unsubscribeOnlineManager: (() => void) | null = null

function isTokenValid(): boolean {
  if (!mercureJwtToken || !tokenExpiry) return false
  return Date.now() + TOKEN_REFRESH_BUFFER_MS < tokenExpiry
}

async function getMercureToken(): Promise<string | null> {
  if (mercureJwtToken && isTokenValid()) {
    return mercureJwtToken
  }

  if (tokenFetchPromise) {
    return tokenFetchPromise
  }

  const requestGeneration = tokenGeneration
  const fetchPromise = (async () => {
    try {
      const response = await api.get<{ token: string }>('/mercure-token')
      if (requestGeneration !== tokenGeneration) {
        return null
      }

      mercureJwtToken = response.data.token
      tokenExpiry = Date.now() + TOKEN_LIFETIME_MS
      return mercureJwtToken
    } catch (error) {
      console.warn('[Mercure] Failed to get token, SSE may not work:', error)
      return null
    }
  })()

  tokenFetchPromise = fetchPromise
  void fetchPromise.then(() => {
    if (tokenFetchPromise === fetchPromise) {
      tokenFetchPromise = null
    }
  })
  return fetchPromise
}

export function clearMercureToken(): void {
  tokenGeneration += 1
  mercureJwtToken = null
  tokenExpiry = null
  tokenFetchPromise = null
}

function buildMercureUrl(topic: string, token?: string, lastEventId?: string | null): string {
  const params = new URLSearchParams({ topic })
  if (token) {
    params.set('authorization', token)
  }
  if (lastEventId) {
    params.set('lastEventID', lastEventId)
  }
  return getFullApiUrl(`/.well-known/mercure?${params.toString()}`)
}

function isActiveConnection(connection: ManagedConnection): boolean {
  return connections.get(connection.topic) === connection && connection.subscriptions.size > 0
}

/**
 * Every subscriber parses `event.data` itself, so the signed media URLs are adopted here, once per
 * message and for every topic, rather than in each of them (AUTHZ-02 E2). The event object is handed
 * on unchanged unless something was adopted — `MessageEvent.data` is read-only, so an adopted
 * message is a new event carrying the same `lastEventId`.
 */
function withSignedMediaUrls(event: MessageEvent): MessageEvent {
  if (typeof event.data !== 'string') return event
  const data = adoptSignedMediaUrlsInJson(event.data)
  if (data === event.data) return event
  return new MessageEvent(event.type, {
    data,
    lastEventId: event.lastEventId,
    origin: event.origin,
  })
}

function dispatchEvent(connection: ManagedConnection, event: MessageEvent): void {
  const delivered = withSignedMediaUrls(event)
  const subscribers = Array.from(connection.subscriptions.values())
  subscribers.forEach(({ handler, closed }) => {
    if (closed) return
    try {
      handler(delivered)
    } catch (error) {
      console.error('[Mercure] Subscriber handler failed:', error)
    }
  })
}

function dispatchRecovery(connection: ManagedConnection): void {
  const subscribers = Array.from(connection.subscriptions.values())
  subscribers.forEach(({ onRecovery, closed }) => {
    if (closed || !onRecovery) return
    try {
      onRecovery()
    } catch (error) {
      console.error('[Mercure] Subscriber recovery failed:', error)
    }
  })
}

function clearConnectionTimers(connection: ManagedConnection): void {
  if (connection.reconnectTimer) {
    clearTimeout(connection.reconnectTimer)
    connection.reconnectTimer = null
  }
  if (connection.tokenRefreshTimer) {
    clearTimeout(connection.tokenRefreshTimer)
    connection.tokenRefreshTimer = null
  }
}

function closeCurrentEventSource(connection: ManagedConnection): void {
  connection.generation += 1
  if (connection.tokenRefreshTimer) {
    clearTimeout(connection.tokenRefreshTimer)
    connection.tokenRefreshTimer = null
  }

  const eventSource = connection.eventSource
  connection.eventSource = null
  eventSource?.close()
}

function nextReconnectDelay(connection: ManagedConnection): number {
  const delay = Math.min(
    RECONNECT_BASE_DELAY_MS * 2 ** connection.reconnectAttempts,
    RECONNECT_MAX_DELAY_MS,
  )
  connection.reconnectAttempts += 1
  return delay
}

function scheduleReconnect(
  connection: ManagedConnection,
  delay: number,
  replacePending: boolean = false,
): void {
  if (!isActiveConnection(connection)) return

  // Unconditional on purpose: the first connect calls openConnection() directly,
  // so every path into scheduleReconnect is already a reconnect. Gating this on
  // "has opened before" skipped recovery when the FIRST open failed (e.g. a 401
  // while the mercureAuthorization cookie is expired but the session is not):
  // the eventual open then carried neither onRecovery nor a lastEventID.
  //
  // Bound to the generation the timer below will open, not to "whichever source
  // opens next": openConnection() bumps `generation` before attaching, so the
  // replacement is always `generation + 1` or later. If the CURRENT source fires
  // onopen between now and the timer (a still-connecting first source, or a
  // browser-managed transient reconnect while the native token refresh fires),
  // that open must not consume the recovery owed to the replacement.
  connection.recoveryAfterGeneration = connection.generation + 1

  if (connection.reconnectTimer) {
    if (!replacePending) return
    clearTimeout(connection.reconnectTimer)
  }

  connection.reconnectTimer = setTimeout(() => {
    connection.reconnectTimer = null
    if (!isActiveConnection(connection) || !onlineManager.isOnline()) return
    void openConnection(connection)
  }, delay)
}

function handlePermanentClose(
  connection: ManagedConnection,
  eventSource: EventSource,
  generation: number,
): void {
  if (
    generation !== connection.generation ||
    connection.eventSource !== eventSource ||
    !isActiveConnection(connection)
  ) {
    return
  }

  connection.eventSource = null
  if (connection.tokenRefreshTimer) {
    clearTimeout(connection.tokenRefreshTimer)
    connection.tokenRefreshTimer = null
  }
  scheduleReconnect(connection, nextReconnectDelay(connection))
}

function attachEventSource(
  connection: ManagedConnection,
  generation: number,
  mercureUrl: string,
  native: boolean,
): void {
  if (generation !== connection.generation || !isActiveConnection(connection)) return

  let eventSource: EventSource
  try {
    eventSource = native
      ? new EventSource(mercureUrl)
      : new EventSource(mercureUrl, { withCredentials: true })
  } catch (error) {
    debug('Failed to create EventSource for topic:', connection.topic, error)
    scheduleReconnect(connection, nextReconnectDelay(connection))
    return
  }

  if (generation !== connection.generation || !isActiveConnection(connection)) {
    eventSource.close()
    return
  }

  connection.eventSource = eventSource
  eventSource.onopen = () => {
    if (generation !== connection.generation) return
    connection.reconnectAttempts = 0
    debug('SSE connected for topic:', connection.topic)
    if (
      connection.recoveryAfterGeneration !== null &&
      generation >= connection.recoveryAfterGeneration
    ) {
      connection.recoveryAfterGeneration = null
      dispatchRecovery(connection)
    }
  }
  eventSource.onmessage = event => {
    if (generation !== connection.generation) return
    if (event.lastEventId) {
      connection.lastEventId = event.lastEventId
    }
    dispatchEvent(connection, event)
  }
  eventSource.onerror = error => {
    debug('SSE error for topic:', connection.topic, error)
    if (eventSource.readyState === EventSource.CLOSED) {
      handlePermanentClose(connection, eventSource, generation)
    }
  }

  if (native) {
    connection.tokenRefreshTimer = setTimeout(() => {
      scheduleReconnect(connection, 0, true)
    }, TOKEN_LIFETIME_MS - TOKEN_REFRESH_BUFFER_MS)
  }
}

async function openConnection(connection: ManagedConnection): Promise<void> {
  if (!isActiveConnection(connection)) return

  closeCurrentEventSource(connection)
  const generation = connection.generation
  const native = isNativePlatform()

  if (!native) {
    attachEventSource(
      connection,
      generation,
      buildMercureUrl(connection.topic, undefined, connection.lastEventId),
      false,
    )
    return
  }

  const token = await getMercureToken()
  if (generation !== connection.generation || !isActiveConnection(connection)) return
  if (!token) {
    scheduleReconnect(connection, nextReconnectDelay(connection))
    return
  }

  attachEventSource(
    connection,
    generation,
    buildMercureUrl(connection.topic, token, connection.lastEventId),
    true,
  )
}

function reconnectAll(): void {
  if (!onlineManager.isOnline()) return

  Array.from(connections.values()).forEach((connection, index) => {
    scheduleReconnect(connection, index * RECONNECT_STAGGER_MS, true)
  })
}

/**
 * Reopen every live subscription with fresh credentials, keeping the subscriptions themselves.
 *
 * For when the server says the claims changed (X-Mercure-Claims on /maintenance-status): an open
 * EventSource keeps the claims it connected with until the hub closes it at token expiry, and the
 * web cookie is HttpOnly, so nothing else would make the new claims take effect.
 */
export function reconnectAllMercureConnections(): void {
  clearMercureToken()
  reconnectAll()
}

function handleVisibilityChange(): void {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now()
    return
  }

  const suspendedFor = hiddenAt === null ? 0 : Date.now() - hiddenAt
  hiddenAt = null
  if (suspendedFor >= VISIBILITY_RECONNECT_THRESHOLD_MS) {
    reconnectAll()
  }
}

function installLifecycleListeners(): void {
  if (lifecycleListenersInstalled) return
  lifecycleListenersInstalled = true

  if (typeof document !== 'undefined') {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  unsubscribeOnlineManager = onlineManager.subscribe(isOnline => {
    if (isOnline) {
      reconnectAll()
    }
  })
}

function removeLifecycleListeners(): void {
  if (!lifecycleListenersInstalled) return
  lifecycleListenersInstalled = false
  hiddenAt = null

  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  }
  unsubscribeOnlineManager?.()
  unsubscribeOnlineManager = null
}

function destroyConnection(connection: ManagedConnection): void {
  if (connections.get(connection.topic) !== connection) return

  connections.delete(connection.topic)
  clearConnectionTimers(connection)
  closeCurrentEventSource(connection)
  connection.subscriptions.forEach(subscription => {
    subscription.closed = true
  })
  connection.subscriptions.clear()
  useEventTopicsStore.getState().removeTopic(connection.topic)

  if (connections.size === 0) {
    removeLifecycleListeners()
  }
}

function createNoopSubscription(): MercureSubscription {
  const record = { closed: false }
  return {
    get closed() {
      return record.closed
    },
    close: () => {
      record.closed = true
    },
  }
}

/**
 * Adds an independent subscriber to a topic while sharing one physical
 * EventSource per topic. The returned handle owns only this subscriber.
 */
export function subscribeToMercureTopic(
  topic: string,
  onMessageCallback: MercureMessageHandler,
  options: MercureSubscriptionOptions = {},
): MercureSubscription {
  const subscriptionsSkipped = import.meta.env.VITE_SKIP_EVENT_SUBSCRIPTIONS === 'true'
  if (subscriptionsSkipped && !__DEV_HARNESS__) {
    return createNoopSubscription()
  }

  let connection = connections.get(topic)
  const isNewConnection = !connection
  if (!connection) {
    connection = {
      topic,
      subscriptions: new Map(),
      eventSource: null,
      reconnectTimer: null,
      tokenRefreshTimer: null,
      generation: 0,
      reconnectAttempts: 0,
      lastEventId: null,
      recoveryAfterGeneration: null,
    }
    connections.set(topic, connection)
  }

  const subscriptionId = nextSubscriptionId
  nextSubscriptionId += 1
  const record: SubscriptionRecord = {
    handler: onMessageCallback,
    onRecovery: options.onRecovery ?? null,
    closed: false,
  }
  connection.subscriptions.set(subscriptionId, record)

  if (isNewConnection) {
    useEventTopicsStore.getState().addTopic(topic)
    if (__DEV_HARNESS__) {
      registerMercureHandler(topic, event => dispatchEvent(connection, event))
    }
    if (!subscriptionsSkipped) {
      installLifecycleListeners()
      void openConnection(connection)
    }
  }

  return {
    get closed() {
      return record.closed
    },
    close: () => {
      if (record.closed) return
      record.closed = true
      connection.subscriptions.delete(subscriptionId)
      if (connection.subscriptions.size === 0) {
        destroyConnection(connection)
      }
    },
  }
}

/** Close every managed web/native stream and subscriber during logout. */
export function closeAllMercureConnections(): void {
  // The cached native token carries claims for the session that is ending (logout) or for the
  // organization being left (switch): the role selectors follow the ACTIVE organization, so
  // reusing it would keep the old organization's issue access until the token expires.
  clearMercureToken()
  Array.from(connections.values()).forEach(destroyConnection)
  removeLifecycleListeners()
  if (__DEV_HARNESS__) {
    clearMercureRegistry()
  }
}

/** @deprecated Use the subscription handle returned by subscribeToMercureTopic. */
export function closeNativeEventSource(topic: string): void {
  const connection = connections.get(topic)
  if (connection) {
    destroyConnection(connection)
  }
}

/** @deprecated Use closeAllMercureConnections for logout teardown. */
export function closeAllNativeEventSources(): void {
  closeAllMercureConnections()
}
